<template>
  <div class="min-h-screen" :class="hideLayout ? 'login-layout' : 'app-layout bg-gray-50'">
    <!-- 登录页面直接显示内容 -->
    <template v-if="hideLayout">
      <router-view></router-view>
    </template>

    <!-- 主应用布局 -->
    <template v-else>
      <div class="flex">
        <!-- 侧边栏 -->
        <aside
          class="sidebar bg-white shadow-lg text-gray-700 fixed h-screen z-10 overflow-hidden border-r border-gray-100"
          :class="{ 'sidebar-expanded': isExpanded, 'sidebar-collapsed': !isExpanded }" @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave">

          <!-- Logo -->
          <div class="p-4 flex items-center justify-center h-16 border-b border-gray-100">
            <div class="flex-shrink-0 flex items-center">
              <div class="logo-icon">
                <i class="fas fa-robot"></i>
              </div>
              <span class="menu-text ml-3 font-semibold text-gray-800 text-lg" v-show="isExpanded">青络服务中心</span>
            </div>
          </div>

          <!-- 导航菜单 -->
          <nav class="mt-6 px-3">
            <div class="space-y-2">
              <!-- 动态生成菜单项 -->
              <template v-for="item in menuItems" :key="item.key">
                <!-- 单个菜单项 -->
                <router-link 
                  v-if="!item.children" 
                  :to="item.path" 
                  class="nav-item" 
                  active-class="nav-item-active"
                >
                  <div class="nav-icon" :class="getIconBgClass(item.iconBg)" :style="getIconBgStyle(item.iconBg)">
                    <i :class="item.icon"></i>
                  </div>
                  <span class="menu-text" v-show="isExpanded">{{ item.title }}</span>
                </router-link>

                <!-- 菜单分组 -->
                <div v-else class="nav-section">
                  <div class="section-divider" v-show="isExpanded">
                    <span class="section-title">{{ item.title }}</span>
                  </div>

                  <router-link 
                    v-for="child in item.children" 
                    :key="child.key"
                    :to="child.path" 
                    class="nav-item" 
                    active-class="nav-item-active"
                  >
                    <div class="nav-icon" :class="getIconBgClass(child.iconBg)" :style="getIconBgStyle(child.iconBg)">
                      <i :class="child.icon"></i>
                    </div>
                    <span class="menu-text" v-show="isExpanded">{{ child.title }}</span>
                  </router-link>
                </div>
              </template>
            </div>
          </nav>
        </aside>

        <!-- 主要内容区域 -->
        <div class="content-wrapper flex-1 flex flex-col"
          :class="{ 'content-wrapper-expanded': isExpanded, 'content-wrapper-collapsed': !isExpanded }">

          <!-- 顶部导航栏 -->
          <header class="top-navbar bg-white h-16 fixed top-0 z-20 border-b border-gray-100"
            :class="{ 'top-navbar-expanded': isExpanded, 'top-navbar-collapsed': !isExpanded }">
            <div class="flex h-full px-6 justify-between items-center">
              <div class="flex items-center">
                <h1 class="text-xl font-semibold text-gray-800">{{ currentPageTitle }}</h1>
              </div>

              <div class="flex items-center space-x-3">
                <!-- 未登录状态 -->
                <template v-if="!isLoggedIn">
                  <router-link to="/login" class="login-btn">
                    <i class="fas fa-sign-in-alt"></i>
                    <span>登录</span>
                  </router-link>
                </template>

                <!-- 已登录状态 -->
                <template v-else>
                  <!-- 个人中心 -->
                  <router-link to="/profile" class="user-menu-btn" title="个人中心">
                    <div class="user-avatar">
                      <i class="fas fa-user-circle"></i>
                    </div>
                    <span class="user-name">{{ userInfo.nickname || '管理员' }}</span>
                  </router-link>

                  <!-- 退出登录 -->
                  <button @click="logout" class="logout-btn" title="退出登录">
                    <i class="fas fa-sign-out-alt"></i>
                    <span>退出</span>
                  </button>
                </template>
              </div>
            </div>
          </header>

          <!-- 页面内容 -->
          <main class="main-content flex-1 overflow-auto">
            <router-view></router-view>
          </main>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import { getUserInfo, logout } from '@/api/user'
import request from '@/api/config'

  export default {
    name: 'App',
    data() {
      return {
        isExpanded: false,
        isLoggedIn: false,
        hideLayout: false,
        userInfo: {
          phone: '',
          nickname: ''
        },
        currentRoute: '',
        expandTimer: null,
        collapseTimer: null,
        resizeTimer: null,
        userPermissions: [],
        menuItems: [],
        windowWidth: window.innerWidth,
        isMobile: window.innerWidth <= 768
      }
    },
    computed: {
      currentPageTitle() {
        // 从当前路由中获取标题，如果没有则使用菜单中的标题
        const title = this.getPageTitleFromRoute(this.currentRoute)
        return title || '青络服务中心'
      }
    },
    watch: {
      '$route': {
        immediate: true,
        handler(to) {
          if (to && to.name) {
            this.currentRoute = to.name.toLowerCase()
          } else {
            this.currentRoute = ''
          }
          // 检查是否需要隐藏布局（如登录页面）
          this.hideLayout = to?.meta?.hideLayout || false
          
          // 如果是无权限页面，确保布局正确
          if (to.path === '/no-permission') {
            this.hideLayout = true
          }
        }
      }
    },
    mounted() {
      // 初始化当前路由
      if (this.$route && this.$route.name) {
        this.currentRoute = this.$route.name.toLowerCase()
      } else {
        this.currentRoute = ''
      }

      // 检查登录状态
      this.checkLoginStatus()

      // 监听登录状态变化
      window.addEventListener('user-login', this.handleUserLogin)
      window.addEventListener('user-logout', this.handleUserLogout)
      
      // 监听窗口大小变化
      window.addEventListener('resize', this.handleResize)
      
      // 初始化响应式状态
      this.updateResponsiveState()
    },

    beforeUnmount() {
      // 移除事件监听器
      window.removeEventListener('user-login', this.handleUserLogin)
      window.removeEventListener('user-logout', this.handleUserLogout)
      window.removeEventListener('resize', this.handleResize)
      
      // 清理定时器
      if (this.expandTimer) {
        clearTimeout(this.expandTimer)
      }
      if (this.collapseTimer) {
        clearTimeout(this.collapseTimer)
      }
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
    },

    methods: {
      // 使用防抖处理鼠标进入事件
      handleMouseEnter() {
        // 在移动端禁用鼠标悬停展开
        if (this.isMobile) {
          return;
        }
        
        clearTimeout(this.collapseTimer);
        this.expandTimer = setTimeout(() => {
          this.isExpanded = true;
        }, 50);
      },

      // 使用防抖处理鼠标离开事件
      handleMouseLeave() {
        // 在移动端禁用鼠标悬停展开
        if (this.isMobile) {
          return;
        }
        
        clearTimeout(this.expandTimer);
        this.collapseTimer = setTimeout(() => {
          this.isExpanded = false;
        }, 100);
      },

      async checkLoginStatus() {
        const token = localStorage.getItem('token')
        if (!token) {
          // 没有token，路由守卫会处理跳转
          this.isLoggedIn = false
          return
        }

        try {
          const result = await getUserInfo()
          this.isLoggedIn = true
          this.userInfo = result.data
          console.log('用户信息:', this.userInfo)
          
          // 加载用户权限
          await this.loadUserPermissions()
        } catch (error) {
          console.error('检查登录状态失败:', error)
          // token无效，清除token，路由守卫会处理跳转
          localStorage.removeItem('token')
          this.isLoggedIn = false
          // 手动触发路由重新检查
          this.$router.push('/login')
        }
      },

      async loadUserPermissions() {
        try {
          // 使用配置好的request实例获取用户权限菜单
          const response = await request.get('/user/permissions')
          console.log('权限API响应:', response)
          
          if (response.code === 200) {
            const permissions = response.data || []
            // 提取权限编码
            this.userPermissions = permissions.map(p => p.permissionCode).filter(code => code)
            console.log('加载的用户权限:', this.userPermissions)
            
            // 构建菜单树
            this.menuItems = this.buildMenuTree(permissions)
            console.log('构建的菜单树:', this.menuItems)
            
            // 设置localStorage中的权限数据
            localStorage.setItem('userPermissions', JSON.stringify(this.userPermissions))
            
            // 同时设置用户角色信息
            if (this.userInfo.roleCode) {
              localStorage.setItem('userRoles', JSON.stringify([this.userInfo.roleCode.toLowerCase()]))
            }
          } else {
            console.warn('获取权限失败，响应格式不正确:', response)
            this.userPermissions = []
            this.menuItems = []
            localStorage.setItem('userPermissions', JSON.stringify([]))
          }
        } catch (error) {
          console.error('加载用户权限失败:', error)
          // 如果API调用失败，使用默认权限确保基本功能可用
          this.userPermissions = ['home']
          this.menuItems = []
          localStorage.setItem('userPermissions', JSON.stringify(this.userPermissions))
        }
      },

      buildMenuTree(permissions) {
        // 只显示类型为菜单(1)且启用(status=1)的权限
        const menuPermissions = permissions.filter(p => p.permissionType === 1 && p.status === 1)
        
        // 按 sortOrder 排序
        menuPermissions.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        
        // 构建树形结构
        const tree = []
        const map = {}
        
        // 先创建所有节点的映射
        menuPermissions.forEach(permission => {
          map[permission.id] = {
            key: permission.permissionCode,
            path: permission.path,
            title: permission.permissionName,
            icon: permission.icon || 'fas fa-folder',
            iconBg: permission.iconBg || '#6b7280',
            permission: permission.permissionCode,
            show: true,
            children: []
          }
        })
        
        // 构建树结构
        menuPermissions.forEach(permission => {
          const node = map[permission.id]
          if (permission.parentId === 0 || !map[permission.parentId]) {
            // 顶级菜单
            tree.push(node)
          } else {
            // 子菜单
            const parent = map[permission.parentId]
            if (parent) {
              parent.children.push(node)
            }
          }
        })
        
        // 移除没有子菜单的children属性
        const cleanTree = (nodes) => {
          return nodes.map(node => {
            if (node.children && node.children.length > 0) {
              node.children = cleanTree(node.children)
              return node
            } else {
              delete node.children
              return node
            }
          })
        }
        
        return cleanTree(tree)
      },

      getPageTitleFromRoute(routeName) {
        if (!routeName) return ''
        
        // 在菜单项中查找标题
        const findTitle = (items, route) => {
          for (const item of items) {
            if (item.key === route.toLowerCase()) {
              return item.title
            }
            if (item.children) {
              const title = findTitle(item.children, route)
              if (title) return title
            }
          }
          return null
        }
        
        const title = findTitle(this.menuItems, routeName)
        
        // 如果在菜单中没找到，使用默认映射
        const defaultTitles = {
          'home': '主页',
          'login': '用户登录',
          'profile': '个人中心',
          'no-permission': '访问受限'
        }
        
        return title || defaultTitles[routeName.toLowerCase()] || ''
      },

      handleUserLogin(event) {
        this.isLoggedIn = true
        this.userInfo = event.detail.userInfo
        // 加载用户权限
        this.loadUserPermissions()
      },

      handleUserLogout() {
        this.isLoggedIn = false
        this.userInfo = { phone: '', nickname: '' }
        this.userPermissions = []
      },

      hasPermission(permissionCode) {
        // 如果没有登录，返回false
        if (!this.isLoggedIn) {
          return false
        }
        
        // 如果是超级管理员，拥有所有权限
        if (this.userInfo.roleCode === 'SUPER_ADMIN') {
          return true
        }
        
        // 检查用户是否有指定权限
        return this.userPermissions.includes(permissionCode)
      },

      // 处理窗口大小变化 - 添加防抖处理
      handleResize() {
        // 清除之前的防抖定时器
        if (this.resizeTimer) {
          clearTimeout(this.resizeTimer)
        }
        
        // 使用防抖处理，避免频繁触发ResizeObserver
        this.resizeTimer = setTimeout(() => {
          this.windowWidth = window.innerWidth
          this.updateResponsiveState()
        }, 100) // 100ms防抖延迟
      },

      // 更新响应式状态
      updateResponsiveState() {
        const wasMobile = this.isMobile
        const newIsMobile = window.innerWidth <= 768
        
        // 只有在状态真正改变时才更新
        if (this.isMobile !== newIsMobile) {
          this.isMobile = newIsMobile
          
          // 如果从桌面切换到移动端，自动收起侧边栏
          if (!wasMobile && this.isMobile) {
            this.isExpanded = false
          }
          
          // 在移动端禁用鼠标悬停展开
          if (this.isMobile) {
            this.isExpanded = false
          }
        }
      },



      async logout() {
        try {
          await logout()
        } catch (error) {
          // 忽略退出登录的错误
        } finally {
          // 清除本地数据
          localStorage.removeItem('token')
          this.isLoggedIn = false
          this.userInfo = { phone: '', nickname: '' }

          // 触发全局登出事件
          window.dispatchEvent(new CustomEvent('user-logout'))

          this.$message.success('退出登录成功')

          // 跳转到登录页
          this.$router.push('/login')
        }
      },

      /**
       * 获取图标背景的CSS类名
       * 如果是Tailwind类名（包括渐变类名），返回类名；否则返回空字符串
       */
      getIconBgClass(iconBg) {
        if (!iconBg) return ''
        
        // 如果是Tailwind类名（以bg-开头），包括渐变类名如 bg-gradient-to-br
        if (iconBg.startsWith('bg-')) {
          // 处理多个Tailwind类名的情况（如 "bg-gradient-to-br from-indigo-400 to-indigo-600"）
          return iconBg
        }
        
        return ''
      },

      /**
       * 获取图标背景的样式对象
       * 处理CSS颜色值、CSS渐变字符串等（不包括Tailwind类名）
       */
      getIconBgStyle(iconBg) {
        if (!iconBg) {
          return { backgroundColor: '#6b7280' }
        }

        // 如果是Tailwind类名，不设置内联样式，让CSS类处理
        if (iconBg.startsWith('bg-')) {
          return {}
        }

        // 如果是CSS渐变字符串（linear-gradient 或 radial-gradient）
        if (iconBg.includes('gradient') || iconBg.startsWith('linear-gradient') || iconBg.startsWith('radial-gradient')) {
          return { background: iconBg }
        }

        // 如果是CSS颜色值（以#开头或rgb/rgba/hsl）
        if (iconBg.startsWith('#') || iconBg.startsWith('rgb') || iconBg.startsWith('hsl')) {
          return { backgroundColor: iconBg }
        }

        // 默认返回灰色
        return { backgroundColor: '#6b7280' }
      }
    }
  }
</script>

<style scoped>

  /* 登录布局 */
  .login-layout {
    overflow: hidden;
  }

  /* 主应用布局 */
  .app-layout {
    overflow-x: hidden;
    min-height: 100vh;
    width: 100%;
  }

  /* 侧边栏样式 */
  .sidebar {
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: width;
    background: #ffffff;
    border-right: 1px solid #e5e7eb;
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    overflow-x: hidden;
    overflow-y: auto;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);
  }

  .sidebar-collapsed {
    width: 4.5rem !important;
  }

  .sidebar-expanded {
    width: 16rem !important;
  }

  /* Logo样式 */
  .logo-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #1e40af;
    color: white;
    font-size: 20px;
    box-shadow: 0 1px 2px rgba(30, 64, 175, 0.2);
  }

  /* 导航项样式 */
  .nav-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    margin: 2px 0;
    border-radius: 12px;
    color: #64748b;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
  }

  .nav-item:hover {
    background: #f3f4f6;
    color: #1e40af;
  }

  .nav-item-active {
    background: #eff6ff;
    color: #1e40af;
    border-left: 3px solid #1e40af;
    font-weight: 600;
  }

  .nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: white;
    font-size: 16px;
    margin-right: 12px;
    flex-shrink: 0;
    /* 默认背景色，但会被Tailwind类名或内联样式覆盖 */
    background-color: #6b7280;
    transition: background-color 0.2s ease, opacity 0.2s ease, background 0.2s ease;
  }

  /* 确保Tailwind背景类名能正确应用，覆盖默认背景色 */
  /* 使用通用选择器匹配所有包含bg-的类名 */
  .nav-icon[class*="bg-"] {
    background-color: transparent !important;
  }

  /* 悬停状态：稍微变暗 */
  .nav-item:hover .nav-icon {
    opacity: 0.85;
  }
  
  /* 激活状态下的悬停：保持不透明 */
  .nav-item-active:hover .nav-icon {
    opacity: 1;
  }

  /* 激活状态：使用主色调，覆盖所有背景样式 */
  .nav-item-active .nav-icon {
    background-color: #1e40af !important;
    background: #1e40af !important;
    opacity: 1;
  }

  /* 激活状态下，如果有Tailwind类名，也需要覆盖 */
  .nav-item-active .nav-icon[class*="bg-"] {
    background-color: #1e40af !important;
    background: #1e40af !important;
  }


  /* 分组样式 */
  .nav-section {
    margin: 24px 0;
  }

  .section-divider {
    margin: 16px 0 12px 0;
    padding: 0 16px;
  }

  .section-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
  }

  /* 菜单文本动画 */
  .menu-text {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
  }

  .sidebar-collapsed .menu-text {
    opacity: 0;
    transform: translateX(-10px);
  }

  .sidebar-expanded .menu-text {
    opacity: 1;
    transform: translateX(0);
  }

  .sidebar-collapsed .nav-item {
    justify-content: center;
  }

  .sidebar-collapsed .nav-icon {
    margin-right: 0;
  }

  .sidebar-collapsed .section-divider {
    height: 0;
    overflow: hidden;
    margin: 8px 0;
  }

  /* 内容区域样式 */
  .content-wrapper {
    transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: margin-left;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
    width: 100%;
  }

  .content-wrapper-collapsed {
    margin-left: 4.5rem !important;
  }

  .content-wrapper-expanded {
    margin-left: 16rem !important;
  }

  /* 顶部导航栏样式 */
  .top-navbar {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    border-bottom: 1px solid #e5e7eb;
    background: #ffffff;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 999;
  }

  .top-navbar-collapsed {
    left: 4.5rem;
  }

  .top-navbar-expanded {
    left: 16rem;
  }

  /* 用户菜单样式 */
  .login-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: #1e40af;
    color: white;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
    transition: background-color 0.2s ease;
  }

  .login-btn:hover {
    background: #1e3a8a;
    color: white;
  }

  .user-menu-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    text-decoration: none;
    color: #374151;
    font-weight: 500;
    font-size: 14px;
    transition: all 0.3s ease;
    background: rgba(243, 244, 246, 0.5);
  }

  .user-menu-btn:hover {
    background: rgba(229, 231, 235, 0.8);
    color: #374151;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #4b5563;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 16px;
  }

  .user-name {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .logout-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    border-radius: 6px;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .logout-btn:hover {
    background: #fee2e2;
    border-color: #fca5a5;
  }

  /* 主内容区域 */
  .main-content {
    height: calc(100vh - 64px);
    background: #f8fafc;
    padding: 0;
    padding-top: 64px;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
  }

  /* 响应式设计 */
  @media (max-width: 1024px) {
    .sidebar-collapsed {
      width: 4rem !important;
    }

    .content-wrapper-collapsed {
      margin-left: 4rem !important;
    }

    .top-navbar-collapsed {
      left: 4rem;
    }

    .user-name {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .sidebar {
      transform: translateX(-100%);
      width: 16rem !important;
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      z-index: 1001;
      transition: transform 0.3s ease;
    }

    .sidebar.sidebar-expanded {
      transform: translateX(0);
    }

    .content-wrapper {
      margin-left: 0 !important;
      width: 100% !important;
    }

    .top-navbar {
      padding: 0 1rem;
      left: 0 !important;
      position: fixed;
      top: 0;
      right: 0;
      z-index: 1000;
      width: 100%;
    }

    .top-navbar-collapsed,
    .top-navbar-expanded {
      left: 0 !important;
    }

    .main-content {
      height: calc(100vh - 64px);
      overflow-x: hidden;
      overflow-y: auto;
      width: 100%;
    }
    
    /* 防止移动端水平滚动 */
    body {
      overflow-x: hidden;
    }
    
    .app-layout {
      overflow-x: hidden;
      width: 100%;
    }
  }

  /* 滚动条样式 */
  .sidebar::-webkit-scrollbar {
    width: 4px;
  }

  .sidebar::-webkit-scrollbar-track {
    background: transparent;
  }

  .sidebar::-webkit-scrollbar-thumb {
    background: rgba(156, 163, 175, 0.3);
    border-radius: 2px;
  }

  .sidebar::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.5);
  }


  /* 全局防止水平溢出 */
  html, body {
    overflow-x: hidden;
    max-width: 100%;
  }

  #app {
    overflow-x: hidden;
    max-width: 100%;
  }
</style>

